import type React from "react";
import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { useProfileContext } from "contexts/ProfileContext";
import { Container } from "routes/patients/PatientProfile/components/Information/components/Journal/Journal";
import { Row } from "routes/patients/PatientProfile/components/Information/components/Rows";
import { usePrimaryInsuranceInfo } from "routes/patients/utils/hooks/usePrimaryInsuranceInfo";
import { useSecondaryInsuranceInfo } from "routes/patients/utils/hooks/useSecondaryInsuranceInfo";
import { getPrimaryInsurancePlan, getSecondaryInsurancePlan } from "routes/patients/utils/insuranceHelper";
import { PhoneNumber } from "shared/atoms/PhoneNumber";
import { MarketTypes } from "types";
import { CurrentPatientContext } from "utils/contexts";
import { isMarket } from "utils/market";

import { exists } from "../components/helpers";

export const PersonalDetails: React.FC = () => {
  const { t } = useTranslation();
  const { patient } = useContext(CurrentPatientContext);
  const { profile } = useProfileContext();
  const primaryInsuranceInfo = usePrimaryInsuranceInfo(patient?.id);
  const primaryInsurancePlan = getPrimaryInsurancePlan(patient?.insuranceData);
  const secondaryInsuranceInfo = useSecondaryInsuranceInfo(patient?.insuranceData);
  const secondaryInsurancePlan = getSecondaryInsurancePlan(patient?.insuranceData);

  const generateRow = (left: string, right: string | React.ReactNode) => {
    return <Row left={left} right={right} />;
  };

  const showPayer = isMarket(MarketTypes.UK);

  return (
    <Container data-testid="personal-details">
      {patient?.first_name && generateRow(t("form.first_name"), patient.first_name)}
      {patient?.last_name && generateRow(t("form.last_name"), patient.last_name)}
      {patient?.personal_number && generateRow(t("patients.medical_records.personal_number"), patient.personal_number)}
      {patient?.address && generateRow(t("form.address"), patient.address)}
      {patient?.zip_code && generateRow(t("form.post"), patient.zip_code)}
      {patient?.city && generateRow(t("form.city"), patient.city)}
      {patient?.country && generateRow(t("form.country"), patient.country)}
      {patient?.phone_number &&
        generateRow(t("form.phone"), <PhoneNumber phone={patient.phone_number} country={patient?.country} />)}
      {patient?.email && generateRow(t("form.email"), patient.email)}
      {patient?.birthday &&
        generateRow(
          t("form.date_of_birth"),
          new Date(patient.birthday).toLocaleDateString(profile?.locale, { timeZone: "UTC" })
        )}
      {exists(patient?.npo_consent) &&
        generateRow(
          t("form.npo_consent"),
          patient.npo_consent === true ? t("patient.answered.true") : t("patient.answered.false")
        )}
      {primaryInsuranceInfo?.name &&
        generateRow(
          secondaryInsuranceInfo
            ? t("patient.personal_details.coverage.primary")
            : t("patient.personal_details.coverage.default"),
          primaryInsuranceInfo.name
        )}
      {primaryInsurancePlan &&
        generateRow(
          secondaryInsuranceInfo
            ? t("patient.personal_details.plan.primary")
            : t("patient.personal_details.plan.default"),
          primaryInsurancePlan
        )}
      {secondaryInsuranceInfo && generateRow(t("patient.personal_details.coverage.secondary"), secondaryInsuranceInfo)}
      {secondaryInsurancePlan && generateRow(t("patient.personal_details.plan.secondary"), secondaryInsurancePlan)}
      {showPayer && patient?.payer_name && generateRow(t("form.payer_name"), patient.payer_name)}
      {showPayer && patient?.payer_category && generateRow(t("form.payer_category"), patient.payer_category)}
    </Container>
  );
};
